import React, { useContext, useEffect, useState } from 'react'
import { Result, Select, Space } from 'antd'
import _ from "lodash"
import { FaRegSmile } from "react-icons/fa"
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar, Line } from "react-chartjs-2";
import { Helmet } from 'react-helmet-async';
import language from "../resources/languages/fa_IR"
import { AntDesign, API } from "../resources/constants"

// Contexts
import GlobalContext from "../contexts/global";
import AxiosContext from "../contexts/axios";
import {AxiosError} from "../interfaces/axios";
import dayjs from "dayjs";

// Interfaces
interface ProvinceType {
    id: string,
    name: string,
    publish: boolean,
    createdAt: Date,
    updatedAt: Date,
}

interface CityType {
    id: string,
    name: string,
    province: ProvinceType,
    publish: boolean,
    createdAt: Date,
    updatedAt: Date,
}

interface UserType {
    id: string,
    firstName: string,
    lastName: string,
    nationalCode: string,
    mobile: string,
    email?: string,
    createdAt: Date,
    updatedAt: Date,
}

interface CompanyType {
    row: number,
    id: string,
    key: string,
    number: string,
    position: number,
    name: string,
    tel: string,
    cities: CityType[],
    user: UserType,
    publish: boolean,
    tickets?: { count: number, seat: number }[]
    seatOne?: number,
    seatTwo?: number,
    seatThree?: number,
    seatLeft?: number,
    carLeft?: number,
    countService?: number,
    priority?: number,
    createdAt: Date,
    updatedAt: Date,
}

interface TicketDayType {
    date: { day: number, month: number, year: number },
    totalAmount: number,
    totalTickets: number,
    totalSeats: number,
    tickets: {
        destination: string,
        totalTickets: number,
        totalSeats: number,
    }[],
}

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend
);
ChartJS.defaults.font.family = 'IRANSansFaNum';
const currency = new Intl.NumberFormat()
const { Pages: { Dashboard: DashboardLanguage }, Share } = language
const { TICKET_MANAGEMENT_COMPANY_ALL, TICKET_MANAGEMENT_TICKET_DAY } = API
const { theme: { token: { colorPrimary }}} = AntDesign

export default function Dashboard() {

    const { user, meta } = useContext(GlobalContext)
    const { axios } = useContext(AxiosContext)
    const [companies, setCompanies] = useState<CompanyType[]>([])
    const [company, setCompany] = useState<string>()
    const [tickets, setTickets] = useState<TicketDayType[]>([])

    useEffect(() => {
        initialAPI().then(response => response)
    }, [])

    useEffect(() => {
        company && getTickets(company).then(response => response)
    }, [company])

    const initialAPI = async () => {
        await getCompanies();
    }

    const getCompanies = async () => {
        return axios.post(TICKET_MANAGEMENT_COMPANY_ALL, { publish: true }).then((response: { data: CompanyType[] }) => {
            const { data: companies } = response
            setCompanies(companies)
            setCompany(companies[0]?.id)
        }).catch((error: AxiosError) => {
            const { response: { data } } = error;
            // @ts-ignore
            message.error(MessageLanguage[data.message]).then(response => response )
            setCompanies([])
        });
    }

    const getTickets = async (company: string) => {
        return axios.get(TICKET_MANAGEMENT_TICKET_DAY + company).then((response: { data: TicketDayType[] }) => {
            const { data: tickets } = response
            setTickets(tickets)
        }).catch((error: AxiosError) => {
            const { response: { data } } = error;
            // @ts-ignore
            message.error(MessageLanguage[data.message]).then(response => response )
            setTickets([])
        });
    }

    return (
      <>
          <Helmet>
              <title>{ DashboardLanguage.title + ' - ' + meta?.title }</title>
          </Helmet>
          <Space direction="vertical" size={50} style={{width:'100%'}}>
              <Select
                  showSearch
                  value={company}
                  style={{width:'200px'}}
                  optionFilterProp="label"
                  placeholder={Share.pleaseSelectedRecord}
                  options={companies.map(({ id, name}) => ({ label: name, value: id}))}
                  onChange={(company) => setCompany(company)}
              />
              <Bar
                  className={'mt-5 mr-15 ml-15'}
                  data={{
                      labels: tickets.map(({ date }) => dayjs(`${date.year}-${date.month}-${date.day}`).tz(process.env.REACT_APP_TZ).format('YYYY/MM/DD')),
                      datasets: [
                          {
                              data: tickets.map(({ totalTickets, totalSeats }) => totalSeats),
                              backgroundColor: "rgb(12, 75, 169)",
                              borderColor: "rgba(12,79,214,1)",
                              borderWidth: 1,
                              hoverBackgroundColor: "rgba(246,234,2,0.9)",
                              hoverBorderColor: "rgba(16,48,90,1)",
                          },
                      ],
                  }}
                  options={{
                      responsive: true,
                      plugins: {
                          title: {
                              display: true,
                              font: {
                                  size: 17,
                              },
                              padding: {
                                  bottom: 30,
                              },
                              text: `${DashboardLanguage.chartTitleSeat} (${_.findLast(companies, { id: company })?.name})`,
                          },
                          tooltip: {
                              displayColors: false,
                              rtl: true,
                              textDirection: 'rtl',
                              callbacks: {
                                  title: () => '',
                                  beforeLabel: () => '',
                                  label: ({ label, dataIndex }) => [
                                      label,
                                      `${DashboardLanguage.total} ${DashboardLanguage.seatTicket(tickets[dataIndex].totalSeats, tickets[dataIndex].totalTickets)}`,
                                      ...tickets[dataIndex].tickets.map(({ destination, totalSeats, totalTickets }) => `${destination} - ${DashboardLanguage.seatTicket(totalSeats, totalTickets)}` )
                                  ],
                                  afterLabel: () => '',
                                  footer: () => '',
                              },
                          },
                          legend: {
                              display: false,
                          },
                      },
                      scales: {
                          x: {
                              ticks: {
                                  font: {
                                      size: 14,
                                  },
                              },
                          },
                          y: {
                              ticks: {
                                  font: {
                                      size: 14,
                                  },
                              },
                          },
                      },
                  }}
              />
              <Line
                  className={'mt-5 mr-15 ml-15'}
                  data={{
                      labels: tickets.map(({ date }) => dayjs(`${date.year}-${date.month}-${date.day}`).tz(process.env.REACT_APP_TZ).format('YYYY/MM/DD')),
                      datasets: [
                          {
                              data: tickets.map(({ totalAmount }) => totalAmount),
                              fill: false,
                              backgroundColor: "rgb(12, 75, 169)",
                              borderColor: "rgba(12,79,214,1)",
                              borderWidth: 2,
                              borderCapStyle: "butt",
                              borderDash: [],
                              borderDashOffset: 0.0,
                              borderJoinStyle: "miter",
                              pointBorderColor: "rgba(28,97,192,1)",
                              pointBackgroundColor: "#fff",
                              pointBorderWidth: 8,
                              pointHoverRadius: 5,
                              pointHoverBackgroundColor: "rgba(4,21,238,0.4)",
                              pointHoverBorderColor: "rgba(16,48,90,1)",
                              pointHoverBorderWidth: 2,
                              pointRadius: 1,
                              pointHitRadius: 10,
                          },
                      ],
                  }}
                  options={{
                      responsive: true,
                      plugins: {
                          title: {
                              display: true,
                              font: {
                                  size: 17,
                              },
                              padding: {
                                  bottom: 30,
                              },
                              text: `${DashboardLanguage.chartTitleAmount} (${_.findLast(companies, { id: company })?.name})`,
                          },
                          tooltip: {
                              displayColors: false,
                              rtl: true,
                              textDirection: 'rtl',
                              callbacks: {
                                  title: () => '',
                                  beforeLabel: () => '',
                                  label: ({ label, dataIndex }) => [
                                      label,
                                      `${currency.format(tickets[dataIndex].totalAmount)} ${Share.rial} `,
                                      ],
                                  afterLabel: () => '',
                                  footer: () => '',
                              },
                          },
                          legend: {
                              display: false,
                          },
                      },
                      scales: {
                          x: {
                              ticks: {
                                  font: {
                                      size: 14,
                                  },
                              },
                          },
                          y: {
                              min: 0,
                              ticks: {
                                  autoSkip: false,
                                  callback: (value) => currency.format(Number(value)),
                                  font: {
                                      size: 14,
                                  },
                              },
                          },
                      },
                  }}
              />
          </Space>
      </>
    )
}
